// ForgotPassword.jsx - Complete forgot password component
import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import axios from 'axios';
import AlertMessage from '../../components/AlertMessage';

// Helper to read any cookie by name (mirrors Login.js / VerifyEmail.js)
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { uidb64, token } = useParams(); // For password reset confirmation page
  const [step, setStep] = useState(1); // 1: request email, 2: reset password
  const [formData, setFormData] = useState({
    email: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL;

  // Create axios instance with credentials so the CSRF cookie is included
  const api = axios.create({
    baseURL: API_URL,
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
    }
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (alert) setAlert(null);
  };

  // Determine if we're on the reset page (has uidb64 and token)
  useEffect(() => {
    if (uidb64 && token) {
      setStep(2);
    }
  }, [uidb64, token]);

  // Prime the CSRF cookie when landing directly on the reset-password page
  // from an email link — no prior backend request has set the cookie yet,
  // so getCookie('csrftoken') would return null and Django rejects the POST.
  useEffect(() => {
    if (step === 2) {
      axios.get(`${API_URL}/health/`, { withCredentials: true }).catch(() => { });
    }
  }, [step, API_URL]);

  // Step 1: Request password reset email
  const handleRequestReset = async (e) => {
    e.preventDefault();
    setAlert(null);
    setLoading(true);

    const csrfToken = getCookie('csrftoken');

    try {
      await api.post(
        '/rest-auth/password-reset/',
        { email: formData.email },
        {
          headers: {
            ...(csrfToken && { 'X-CSRFToken': csrfToken }),
          }
        }
      );

      setAlert({
        type: 'success',
        message: 'If an account exists with this email, you will receive password reset instructions.',
        title: 'Email Sent!',
        duration: 2500
      });

      setTimeout(() => {
        navigate('/login');
      }, 2500);

    } catch (err) {
      // Don't reveal if email exists or not for security
      setAlert({
        type: 'success',
        message: 'If an account exists with this email, you will receive password reset instructions.',
        title: 'Email Sent!',
        duration: 2500
      });

      setTimeout(() => {
        navigate('/login');
      }, 2500);
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Reset password with token
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setAlert(null);
    setLoading(true);

    // Validate passwords match
    if (formData.newPassword !== formData.confirmPassword) {
      setAlert({
        type: 'error',
        message: 'Passwords do not match. Please try again.',
        title: 'Validation Error',
        duration: 2500
      });
      setLoading(false);
      return;
    }

    // Validate password strength
    if (formData.newPassword.length < 8) {
      setAlert({
        type: 'error',
        message: 'Password must be at least 8 characters long.',
        title: 'Validation Error',
        duration: 2500
      });
      setLoading(false);
      return;
    }

    const csrfToken = getCookie('csrftoken');
    try {
      await api.post(
        `/rest-auth/password/reset/confirm/`,
        {
          uid: uidb64,
          token: token,
          new_password1: formData.newPassword,
          new_password2: formData.confirmPassword
        },
        {
          headers: {
            ...(csrfToken && { 'X-CSRFToken': csrfToken }),
          }
        }
      );

      setAlert({
        type: 'success',
        message: 'Your password has been reset successfully! You can now log in with your new password.',
        title: 'Password Reset Complete!',
        duration: 2500
      });

      setTimeout(() => {
        navigate('/login');
      }, 2500);

    } catch (err) {
      if (err.response) {
        const data = err.response.data;
        if (data.password) {
          setAlert({
            type: 'error',
            message: Array.isArray(data.password) ? data.password[0] : 'Invalid password. Please try again.',
            title: 'Password Error',
            duration: 2500
          });
        } else if (data.token) {
          setAlert({
            type: 'error',
            message: 'The reset link is invalid or has expired. Please request a new one.',
            title: 'Invalid Link',
            duration: 2500
          });
        } else {
          setAlert({
            type: 'error',
            message: data.detail || 'Failed to reset password. The link may be invalid or expired.',
            title: 'Reset Failed',
            duration: 2500
          });
        }
      } else if (err.request) {
        setAlert({
          type: 'error',
          message: 'Unable to connect to the server. Please try again later.',
          title: 'Connection Error',
          duration: 2500
        });
      } else {
        setAlert({
          type: 'error',
          message: 'An unexpected error occurred. Please try again.',
          title: 'Error',
          duration: 2500
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <>
      {alert && (
        <AlertMessage
          type={alert.type}
          message={alert.message}
          title={alert.title}
          duration={alert.duration}
          onClose={() => setAlert(null)}
        />
      )}

      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 px-4 py-12">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                {step === 1 ? 'Forgot Password?' : 'Reset Password'}
              </h2>
              <p className="text-gray-600">
                {step === 1
                  ? 'Enter your email to receive reset instructions'
                  : 'Enter your new password below'}
              </p>
            </div>

            {step === 1 ? (
              /* Step 1: Email Request Form */
              <form onSubmit={handleRequestReset} className="space-y-6">
                {/* Email Input */}
                <div className="space-y-2">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                      </svg>
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      disabled={loading}
                      className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors disabled:opacity-50"
                      placeholder="Enter your email address"
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </span>
                  ) : (
                    'Send Reset Instructions'
                  )}
                </button>
              </form>
            ) : (
              /* Step 2: Reset Password Form */
              <form onSubmit={handleResetPassword} className="space-y-6">
                {/* New Password Input */}
                <div className="space-y-2">
                  <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                    New Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <input
                      id="newPassword"
                      name="newPassword"
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                      required
                      value={formData.newPassword}
                      onChange={handleChange}
                      disabled={loading}
                      className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors disabled:opacity-50"
                      placeholder="Enter new password"
                      minLength="8"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center disabled:opacity-50"
                      onClick={togglePasswordVisibility}
                      disabled={loading}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? (
                        <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                          <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
                          <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Password must be at least 8 characters long
                  </p>
                </div>

                {/* Confirm Password Input */}
                <div className="space-y-2">
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      autoComplete="new-password"
                      required
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      disabled={loading}
                      className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors disabled:opacity-50"
                      placeholder="Confirm new password"
                      minLength="8"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center disabled:opacity-50"
                      onClick={toggleConfirmPasswordVisibility}
                      disabled={loading}
                      aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    >
                      {showConfirmPassword ? (
                        <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                          <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
                          <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                {/* Password strength indicator */}
                {formData.newPassword && (
                  <div className="space-y-1">
                    <div className="flex items-center space-x-1">
                      <div className={`h-2 w-1/4 rounded-full ${formData.newPassword.length >= 8 ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                      <div className={`h-2 w-1/4 rounded-full ${/[A-Z]/.test(formData.newPassword) ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                      <div className={`h-2 w-1/4 rounded-full ${/[0-9]/.test(formData.newPassword) ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                      <div className={`h-2 w-1/4 rounded-full ${/[^A-Za-z0-9]/.test(formData.newPassword) ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                    </div>
                    <p className="text-xs text-gray-500">
                      Password strength: {
                        formData.newPassword.length >= 8 &&
                          /[A-Z]/.test(formData.newPassword) &&
                          /[0-9]/.test(formData.newPassword) &&
                          /[^A-Za-z0-9]/.test(formData.newPassword)
                          ? 'Strong'
                          : formData.newPassword.length >= 8
                            ? 'Medium'
                            : 'Weak'
                      }
                    </p>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Resetting...
                    </span>
                  ) : (
                    'Reset Password'
                  )}
                </button>
              </form>
            )}

            {/* Navigation Links */}
            <div className="text-center pt-6 border-t border-gray-200 mt-8">
              <p className="text-sm text-gray-600">
                <Link
                  to="/login"
                  className="font-medium text-green-600 hover:text-green-700 transition-colors"
                >
                  ← Back to Login
                </Link>
              </p>
              {step === 1 && (
                <p className="text-sm text-gray-600 mt-2">
                  Don't have an account?{' '}
                  <Link
                    to="/signup"
                    className="font-medium text-green-600 hover:text-green-700 transition-colors"
                  >
                    Sign up
                  </Link>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ForgotPassword;